/**
 * Servicio para gestionar el dólar observado diario
 * Actualiza una vez al día desde mindicador.cl y guarda en configuración
 */
import { mindicadorService } from './mindicadorService';
import apiClient from './apiClient';

const DOLAR_CONFIG_KEY = 'tasa_cambio_usd_clp';
const CACHE_DURATION = 12 * 60 * 60 * 1000; // 12 horas en ms

class DolarObservadoService {
  constructor() {
    this.cache = {
      value: null,
      lastUpdate: null,
      isLoading: false
    };
  }

  /**
   * Obtiene el dólar observado desde configuración (y actualiza si expiró)
   */
  async getDolarObservado() {
    try {
      // Primero cargar desde configuración
      const configValue = await this.loadFromConfiguration();
      
      if (configValue && this.isConfigValueValid(configValue)) {
        // Cache válido - usar valor existente
        this.updateCache(configValue.valor, new Date(configValue.fecha_actualizacion).getTime());
        return configValue.valor;
      }

      // Cache expirado o no existe - actualizar desde mindicador
      console.log('💱 Cache de dólar expirado (12h), actualizando desde mindicador...');
      return await this.updateFromMindicador();

    } catch (error) {
      console.error('Error obteniendo dólar observado:', error);
      // Fallback a valor por defecto
      return 0;
    }
  }

  /**
   * Verifica si el cache local es válido (menos de 12h)
   */
  isCacheValid() {
    if (!this.cache.value || !this.cache.lastUpdate) return false;
    
    const now = Date.now();
    return (now - this.cache.lastUpdate) < CACHE_DURATION;
  }

  /**
   * Verifica si el valor de configuración es válido (menos de 12h)
   */
  isConfigValueValid(configValue) {
    if (!configValue || !configValue.fecha_actualizacion) return false;
    
    const lastUpdate = new Date(configValue.fecha_actualizacion).getTime();
    const now = Date.now();
    return (now - lastUpdate) < CACHE_DURATION;
  }

  /**
   * Carga el valor desde la configuración global
   */
  async loadFromConfiguration() {
    try {
      const response = await apiClient.get(`/api/configuraciones/global/${DOLAR_CONFIG_KEY}`);
      
      if (response.data?.success && response.data.data) {
        const config = response.data.data;
        return {
          valor: parseFloat(config.valor) || null,
          fecha_actualizacion: config.fecha_actualizacion
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error cargando dólar desde configuración:', error);
      return null;
    }
  }

  /**
   * Actualiza el dólar desde mindicador.cl y lo guarda en configuración
   */
  async updateFromMindicador() {
    if (this.cache.isLoading) {
      // Si ya se está cargando, esperar un poco y usar cache actual
      return this.cache.value || 0;
    }

    try {
      this.cache.isLoading = true;
      
      // Obtener valor actual de mindicador
      const newValue = await mindicadorService.getDolarObservado();
      
      // Guardar en configuración global
      await this.saveToConfiguration(newValue);
      
      // Actualizar cache local
      this.updateCache(newValue, Date.now());
      
      console.log(`💱 Dólar observado actualizado: $${newValue}`);
      
      return newValue;

    } catch (error) {
      console.error('Error actualizando dólar desde mindicador:', error);
      
      // Si falla, usar valor actual del cache o fallback
      return this.cache.value || 0;
      
    } finally {
      this.cache.isLoading = false;
    }
  }

  /**
   * Guarda el valor en la configuración global (upsert)
   */
  async saveToConfiguration(value) {
    try {
      const configData = {
        clave: DOLAR_CONFIG_KEY,
        valor: value.toString(),
        descripcion: 'Tasa de cambio USD a CLP (dólar observado)',
        tipo: 'number',
        fecha_actualizacion: new Date().toISOString()
      };

      // Intentar PUT (actualizar) primero, si falla hacer POST (crear)
      try {
        await apiClient.put(`/api/configuraciones/global/${DOLAR_CONFIG_KEY}`, configData);
      } catch (putError) {
        if (putError.response?.status === 404) {
          // No existe, crear nuevo
          await apiClient.post('/api/configuraciones/global', configData);
        } else {
          throw putError;
        }
      }
      
    } catch (error) {
      console.error('Error guardando dólar en configuración:', error);
      throw error;
    }
  }

  /**
   * Actualiza el cache local
   */
  updateCache(value, timestamp) {
    this.cache = {
      value: parseFloat(value),
      lastUpdate: timestamp,
      isLoading: false
    };
  }

  /**
   * Fuerza una actualización (para uso manual/admin)
   */
  async forceUpdate() {
    this.cache.lastUpdate = null; // Invalidar cache
    return await this.updateFromMindicador();
  }

  /**
   * Obtiene información del estado del cache
   */
  getCacheInfo() {
    return {
      value: this.cache.value,
      lastUpdate: this.cache.lastUpdate,
      isValid: this.isCacheValid(),
      nextUpdate: this.cache.lastUpdate ? new Date(this.cache.lastUpdate + CACHE_DURATION) : null
    };
  }
}

// Instancia singleton
export const dolarObservadoService = new DolarObservadoService();
export default dolarObservadoService;